import React, { useState } from 'react'
import { PencilSquareIcon, FlagIcon } from '@heroicons/react/24/outline'
import { apiPatch } from '~/util/api'
import { Button } from '~/lib/ui/button'
import ReminderDialog from './ReminderDialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import useTranslation from '../shared/TranslationProvider'
import { useFormatTime } from '~/hooks/useFormatTime'
import { InjectEventStartTime } from '../home/Reminders'
import parse from 'html-react-parser'

const IncompleteReminder = ({ reminder }) => {
  const { t, locale } = useTranslation('ReminderDialog')
  const { t: tc } = useTranslation('')
  const formatTime = useFormatTime(locale)

  const {
    content,
    due_date: dueDate,
    linked_record_path: linkedRecordPath,
    linked_record_text: linkedRecordText,
    webinar_link: webinarLink,
    saved_recruiter_id: savedRecruiterId,
    event_start_time: eventStartTime,
  } = reminder

  const isAutoCreated = linkedRecordPath !== null && linkedRecordText !== null

  const momentDueDate = dueDate ? moment(dueDate).locale(locale) : null
  const todayOrTomorrow = momentDueDate?.calendar(null, {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
  })

  const isTodayOrTomorrow = ['Today', 'Tomorrow'].includes(todayOrTomorrow)
  const isToday = todayOrTomorrow === 'Today'

  const due = dueDate
    ? isTodayOrTomorrow
      ? todayOrTomorrow
      : formatTime(dueDate, 'short', locale)
    : t('not_set')

  const dueDateClass = isToday ? 'text-[#E10000]' : ''

  const isRecruiterFollowUp =
    savedRecruiterId && content.startsWith('Follow up with ')
  const recruiterName = isRecruiterFollowUp
    ? // Extract the recruiter name from the content
      content.slice('Follow up with '.length)
    : null

  // Translate 'Today' and 'Tomorrow' for display
  const translateDueLabel = (label) => {
    if (label === 'Today') return tc('today')
    if (label === 'Tomorrow') return tc('tomorrow')
    return label
  }

  return (
    <div className="flex min-h-48 flex-col gap-2 rounded-lg border border-solid border-silver bg-white p-4">
      <div className="flex flex-shrink flex-row justify-between border-b border-silver pb-3">
        <div className="flex flex-row items-center justify-start gap-x-2 text-sm">
          <div className={dueDateClass}>
            {t('due_date')} {translateDueLabel(due)}
          </div>
          {isToday && <FlagIcon className="h-4 fill-[#E10000]" />}
        </div>
        {!isAutoCreated ? (
          <ReminderDialog reminder={reminder} locale={locale}>
            <PencilSquareIcon className="h-6 cursor-pointer" />
          </ReminderDialog>
        ) : (
          <div className="h-6"></div>
        )}
      </div>
      <div className="flex-grow content-center">
        {isRecruiterFollowUp ? (
          <>
            {t('follow_up_with')}
            <a
              href={`/job-search/recruiters/my_recruiters?expand=${savedRecruiterId}`}
              className="!underline"
            >
              {recruiterName}
            </a>
          </>
        ) : (
          content &&
          parse(InjectEventStartTime(content, eventStartTime, formatTime))
        )}
      </div>
      <div className="flex flex-row justify-end gap-x-12 [&_a]:inline-block">
        {linkedRecordText && linkedRecordPath && !isRecruiterFollowUp && (
          <a href={linkedRecordPath}>{linkedRecordText}</a>
        )}
        {webinarLink && (
          <a
            href={webinarLink}
            target="_blank"
            className="text-coach-green-cta"
            rel="noopener noreferrer"
          >
            {t('join_now')}
          </a>
        )}
        <CompleteReminderButton reminder={reminder} />
      </div>
    </div>
  )
}
export default IncompleteReminder

const CompleteReminderButton = ({ reminder }) => {
  const { t } = useTranslation('ReminderDialog')
  const [isOpen, setIsOpen] = useState(false)

  const {
    id: reminderId,
    skill_name: skillName,
    document_id: documentId,
  } = reminder

  const queryClient = useQueryClient()

  const handleCompleteReminder = async ({ promoteSkill = false }) => {
    const data = { reminder: { promote_skill: promoteSkill } }

    const res = await apiPatch(`/reminders/${reminderId}/complete`, data)
    if (res.status === 200) {
      setIsOpen(false)
      queryClient.invalidateQueries(['reminders'])
      return res
    } else {
      throw new Error('res not ok', res)
    }
  }

  if (!(skillName && documentId)) {
    return (
      <Button variant="link" onClick={handleCompleteReminder}>
        {t('complete')}
      </Button>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="link">{t('complete')}</Button>
      </DialogTrigger>
      <DialogContent
        className="site-info-dialog roboto max-w-xl"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="font-roboto-slab text-xl tracking-[0.12px]">
            {t('promote_skill')}
          </DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-4">
          <p>
            {t('like_to_add_your_skill_to_skills_inventory', { skillName })}
          </p>
          <div className="flex flex-row justify-between pt-4">
            <Button onClick={handleCompleteReminder}>{t('complete')}</Button>
            <Button
              onClick={() => handleCompleteReminder({ promoteSkill: true })}
            >
              {t('complete_add_to_skills_inventory')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
